import type { HrZoneId } from '@/lib/heartRateZones';
import { findGpsCardioExercise, isGpsCardioWorkoutType } from '@/lib/gpsCardioWorkout';
import type { Workout } from '@/hooks/useWorkouts';

export type PersonalPaceProfile = {
  z1: number;
  z2: number;
  z3: number;
  z4: number;
  z5: number;
  /** Median easy-run pace used as the anchor (sec/km). */
  anchorPaceSecPerKm: number;
  sampleCount: number;
};

export type RunPaceSample = {
  distanceKm: number;
  durationSeconds: number;
  paceSecPerKm: number;
};

const formatPaceSec = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}/km`;
};

export function runPaceFromExercise(ex: {
  distance_km?: number | null;
  duration_seconds?: number | null;
  notes?: string | null;
}): RunPaceSample | null {
  const distanceKm = Number(ex.distance_km || 0);
  if (!Number.isFinite(distanceKm) || distanceKm < 0.5) return null;

  let durationSeconds = Number(ex.duration_seconds || 0);
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    try {
      const parsed = ex.notes ? JSON.parse(ex.notes) : null;
      durationSeconds = Number(parsed?.duration_seconds ?? parsed?.elapsedSeconds ?? 0);
    } catch {
      durationSeconds = 0;
    }
  }
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return null;

  const paceSecPerKm = durationSeconds / distanceKm;
  if (paceSecPerKm < 120 || paceSecPerKm > 900) return null;

  return { distanceKm, durationSeconds, paceSecPerKm };
}

/** Collect recent outdoor GPS run paces from workout history. */
export function collectRecentRunPaceSamples(workouts: Workout[], limit = 12): RunPaceSample[] {
  const samples: RunPaceSample[] = [];
  const sorted = [...workouts]
    .filter((w) => w.completed_at)
    .sort(
      (a, b) =>
        new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime(),
    );

  for (const w of sorted) {
    if (samples.length >= limit) break;
    if (!isGpsCardioWorkoutType(w.workout_type)) continue;
    const ex = findGpsCardioExercise(w.exercises);
    if (!ex) continue;
    const sample = runPaceFromExercise(ex);
    if (sample && sample.distanceKm >= 1) samples.push(sample);
  }
  return samples;
}

/** Build personal pace zones from recent runs (median easy pace as Z2 anchor). */
export function buildPersonalPaceProfile(samples: RunPaceSample[]): PersonalPaceProfile | null {
  const valid = samples.filter((s) => s.distanceKm >= 1 && s.paceSecPerKm >= 150 && s.paceSecPerKm <= 720);
  if (valid.length < 2) return null;

  const sorted = [...valid].sort((a, b) => a.paceSecPerKm - b.paceSecPerKm);
  const median = sorted[Math.floor(sorted.length / 2)].paceSecPerKm;

  return {
    anchorPaceSecPerKm: Math.round(median),
    z1: Math.round(median * 1.14),
    z2: Math.round(median),
    z3: Math.round(median * 0.93),
    z4: Math.round(median * 0.86),
    z5: Math.round(median * 0.79),
    sampleCount: valid.length,
  };
}

export function buildPersonalPaceProfileFromWorkouts(workouts: Workout[]): PersonalPaceProfile | null {
  return buildPersonalPaceProfile(collectRecentRunPaceSamples(workouts));
}

export function paceForPersonalZone(zone: HrZoneId, profile: PersonalPaceProfile): number {
  return profile[zone];
}

/** Map a target pace to the closest personal zone (faster pace → higher zone). */
export function inferZoneFromPersonalPace(paceSec: number, profile: PersonalPaceProfile): HrZoneId {
  const ordered: Array<{ id: HrZoneId; pace: number }> = [
    { id: 'z5', pace: profile.z5 },
    { id: 'z4', pace: profile.z4 },
    { id: 'z3', pace: profile.z3 },
    { id: 'z2', pace: profile.z2 },
    { id: 'z1', pace: profile.z1 },
  ];
  for (const row of ordered) {
    if (paceSec <= row.pace + 10) return row.id;
  }
  return 'z1';
}

export function personalPaceTableForCoach(profile: PersonalPaceProfile): string {
  return [
    `Z1 easy: ${formatPaceSec(profile.z1)}`,
    `Z2 conversational: ${formatPaceSec(profile.z2)}`,
    `Z3 tempo: ${formatPaceSec(profile.z3)}`,
    `Z4 threshold: ${formatPaceSec(profile.z4)}`,
    `Z5 interval: ${formatPaceSec(profile.z5)}`,
    `(Based on ${profile.sampleCount} recent runs; anchor ${formatPaceSec(profile.anchorPaceSecPerKm)})`,
  ].join('\n');
}
